/**
 * DebugToolsPage - Debug and Test Tools for admins
 * PROMPT 47: Full Data Reset for testing/demo preparation
 */
import { useState } from "react";
import { toast } from "sonner";
import api, { extractErrorMessage } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import {
  Bug,
  Trash2,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Database,
  Shield,
  Upload,
  Sparkles,
} from "lucide-react";

const CONFIRMATION_PHRASE = "DELETE ALL DATA";

const DATA_TO_WIPE = [
  { collection: "clients", label: "Clients", icon: "👥" },
  { collection: "staff", label: "Crew", icon: "👷" },
  { collection: "locations", label: "Locations", icon: "📍" },
  { collection: "task_definitions", label: "Task Definitions", icon: "📋" },
  { collection: "projects", label: "Projects", icon: "📁" },
  { collection: "shifts", label: "Shifts", icon: "⏰" },
  { collection: "shift_assignments", label: "Shift Assignments", icon: "📅" },
  { collection: "task_assignments", label: "Task Assignments", icon: "✅" },
  { collection: "quotes", label: "Quotes", icon: "💰" },
  { collection: "invoices", label: "Invoices", icon: "🧾" },
];

const PRESERVED_DATA = [
  { label: "Platform Settings", icon: "⚙️" },
  { label: "Email Templates", icon: "📧" },
  { label: "Company Profile / Branding", icon: "🏢" },
  { label: "System Configuration", icon: "🔧" },
];

const DEMO_DATA_CONTENTS = [
  { type: "Clients", count: 10, description: "European event companies" },
  { type: "Locations", count: 15, description: "Venues across Europe" },
  { type: "Task Definitions", count: 33, description: "Event production tasks" },
  { type: "Crew", count: 20, description: "Multi-skilled crew members" },
];

export default function DebugToolsPage() {
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [wiping, setWiping] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);
  
  // Demo data loading state
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [demoResult, setDemoResult] = useState(null);

  const canWipe = acknowledged && confirmPhrase === CONFIRMATION_PHRASE;

  const handleWipeData = async () => {
    if (!canWipe) return;

    setWiping(true);
    setWipeResult(null);

    try {
      const response = await api.post("/debug/wipe-all-data", {
        confirmation_phrase: confirmPhrase,
      });

      setWipeResult({
        success: true,
        message: response.data.message,
        deleted: response.data.deleted,
      });

      toast.success("All operational data has been wiped");
    } catch (err) {
      const errorMsg = extractErrorMessage(err);
      setWipeResult({
        success: false,
        message: errorMsg,
      });
      toast.error(`Wipe failed: ${errorMsg}`);
    } finally {
      setWiping(false);
    }
  };

  const handleLoadDemoData = async () => {
    setLoadingDemo(true);
    setDemoResult(null);

    try {
      const response = await api.post("/debug/load-demo-data");

      setDemoResult({
        success: response.data.success,
        message: response.data.message,
        results: response.data.results,
      });

      if (response.data.success) {
        toast.success("Demo data loaded successfully!");
      } else {
        toast.warning("Demo data loaded with some errors");
      }
    } catch (err) {
      const errorMsg = extractErrorMessage(err);
      setDemoResult({
        success: false,
        message: errorMsg,
      });
      toast.error(`Failed to load demo data: ${errorMsg}`);
    } finally {
      setLoadingDemo(false);
    }
  };

  const resetDialog = () => {
    setShowWipeDialog(false);
    setAcknowledged(false);
    setConfirmPhrase("");
    setWipeResult(null);
  };

  return (
    <div className="space-y-6" data-testid="debug-tools-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Bug className="h-8 w-8 text-yellow-500" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Debug / Test Tools</h1>
          <p className="text-sm text-muted-foreground">
            Development and testing utilities. Use with caution.
          </p>
        </div>
      </div>

      {/* Warning Banner */}
      <Alert variant="destructive" className="border-yellow-500/50 bg-yellow-500/10">
        <AlertTriangle className="h-5 w-5 text-yellow-500" />
        <AlertTitle className="text-yellow-500">Testing Environment Only</AlertTitle>
        <AlertDescription className="text-yellow-500/80">
          These tools are intended for testing and demo preparation. 
          Actions performed here may be irreversible. Ensure you have backups if needed.
        </AlertDescription>
      </Alert>

      {/* Full Data Reset Card */}
      <Card className="border-red-500/30 bg-red-500/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-400">
            <Trash2 className="h-5 w-5" />
            Full Data Reset
          </CardTitle>
          <CardDescription>
            Wipe all operational data to reset the platform to a clean state.
            Useful for testing and demo preparation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* What will be deleted */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-red-400 flex items-center gap-2">
              <Database className="h-4 w-4" />
              Data that WILL be deleted:
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {DATA_TO_WIPE.map((item) => (
                <div
                  key={item.collection}
                  className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg"
                >
                  <span>{item.icon}</span>
                  <span className="text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* What will be preserved */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-green-400 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Data that will be PRESERVED:
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {PRESERVED_DATA.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/20 rounded-lg"
                >
                  <span>{item.icon}</span>
                  <span className="text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Wipe Button */}
          <Button
            variant="destructive"
            size="lg"
            onClick={() => setShowWipeDialog(true)}
            className="w-full md:w-auto"
            data-testid="open-wipe-dialog-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Wipe All Data
          </Button>
        </CardContent>
      </Card>

      {/* Load Demo Data Card */}
      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-400">
            <Sparkles className="h-5 w-5" />
            Load Demo Data
          </CardTitle>
          <CardDescription>
            Populate the platform with realistic demo data for testing and demonstrations.
            Recommended after wiping data to quickly set up a demo environment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Demo data contents */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-blue-400 flex items-center gap-2">
              <Database className="h-4 w-4" />
              Demo data includes:
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {DEMO_DATA_CONTENTS.map((item) => (
                <div
                  key={item.type}
                  className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg"
                >
                  <p className="text-xl font-bold text-blue-400">{item.count}</p>
                  <p className="text-sm font-medium">{item.type}</p>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Result display */}
          {demoResult && (
            <div className={`p-4 rounded-lg ${demoResult.success ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
              <div className="flex items-center gap-2 mb-2">
                {demoResult.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                )}
                <span className={`font-medium ${demoResult.success ? "text-green-400" : "text-red-400"}`}>
                  {demoResult.success ? "Demo Data Loaded" : "Load Failed"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{demoResult.message}</p>
              {demoResult.results && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {Object.entries(demoResult.results).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}:</span>
                      <span className="text-green-400">{value.created} created</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Load Button */}
          <Button
            size="lg"
            onClick={handleLoadDemoData}
            disabled={loadingDemo}
            className="w-full md:w-auto bg-blue-600 hover:bg-blue-700"
            data-testid="load-demo-data-btn"
          >
            {loadingDemo ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading Demo Data...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Load Demo Data
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Wipe Confirmation Dialog */}
      <AlertDialog open={showWipeDialog} onOpenChange={resetDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-6 w-6" />
              Confirm Full Data Wipe
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              {wipeResult ? (
                // Show result
                <div className={`p-4 rounded-lg ${wipeResult.success ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {wipeResult.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                    )}
                    <span className={`font-medium ${wipeResult.success ? "text-green-400" : "text-red-400"}`}>
                      {wipeResult.success ? "Data Wipe Complete" : "Wipe Failed"}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{wipeResult.message}</p>
                  {wipeResult.deleted && (
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      {Object.entries(wipeResult.deleted).map(([key, count]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}:</span>
                          <span className="text-green-400">{count} deleted</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Show confirmation steps
                <>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-red-400 font-medium">
                      ⚠️ This action is IRREVERSIBLE
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      All operational data including clients, crew, locations, projects, 
                      shifts, assignments, quotes, and invoices will be permanently deleted.
                    </p>
                  </div>

                  {/* Step 1: Acknowledgment checkbox */}
                  <div className="space-y-4 pt-2">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="acknowledge"
                        checked={acknowledged}
                        onCheckedChange={(checked) => setAcknowledged(checked === true)}
                        data-testid="acknowledge-checkbox"
                      />
                      <Label
                        htmlFor="acknowledge"
                        className="text-sm leading-tight cursor-pointer"
                      >
                        I understand that this will delete all operational data and 
                        this action cannot be undone.
                      </Label>
                    </div>

                    {/* Step 2: Type confirmation phrase */}
                    <div className="space-y-2">
                      <Label htmlFor="confirm-phrase" className="text-sm">
                        Type <code className="bg-red-500/20 px-2 py-0.5 rounded text-red-400">{CONFIRMATION_PHRASE}</code> to confirm:
                      </Label>
                      <Input
                        id="confirm-phrase"
                        value={confirmPhrase}
                        onChange={(e) => setConfirmPhrase(e.target.value.toUpperCase())}
                        placeholder="Type the phrase..."
                        disabled={!acknowledged}
                        className={!acknowledged ? "opacity-50" : ""}
                        data-testid="confirm-phrase-input"
                      />
                    </div>
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={resetDialog}>
              {wipeResult ? "Close" : "Cancel"}
            </AlertDialogCancel>
            {!wipeResult && (
              <Button
                variant="destructive"
                onClick={handleWipeData}
                disabled={!canWipe || wiping}
                data-testid="confirm-wipe-btn"
              >
                {wiping ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wiping Data...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Permanently Delete All Data
                  </>
                )}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
